import { cloneDeep } from 'lodash';
import { isLogicFollowSA } from '@/src/utils';
import { IDynamicTagGroup } from '..';

const SLCommon = Mach.requireModule('SLCommon');
const picWidth = 120;

/**
   * 三星特殊机型会影响富文本、Native桥计算的结果
   * @returns
   */
export const isUnusualMobileType = () => {
  const { deviceModel } = Mach.env;
  if (['samsung@SM-A5360', 'samsung@SM-S9010'].includes(deviceModel)) {
    return true;
  } else {
    return false;
  }
};

/**
   * 获取二维数组中type为shopName的数据
   * @param content
   * @returns
   */
const getShopName = (content: any) => {
  const shopNameList = content.flatMap((row) => row.filter((item) => item.type === 'shopName'));
  const shopName = shopNameList[0].data;
  return shopName;
};

const replenishShopNameWithIcon = (content: any) => {
  let featureValue; let
    pickUpIconValue;
  let modifiedContent = cloneDeep(content);
  modifiedContent = modifiedContent.map((row: any[]) => row.map((item) => {
    if (item.type === 'featureLabel') {
      featureValue = item.data.featureLabel;
    } else if (item.type === 'pickUpIcon') {
      pickUpIconValue = item.data.pickUpIcon;
    }
    if (item.type === 'shopName') {
      item.data.featureLabel = featureValue;
      item.data.pickUpIcon = pickUpIconValue;
    }
    return item;
  }));
  return modifiedContent;
};

const hasNearRestTime = (content: any) => content.some((subArray) => subArray.some((item) => item.type === 'nearClosingTime'));
// 评分、人均、销量行判定
const hasScoreLine = (content: any) => content.some((subArray) => subArray.some((item) => ['shopScore', 'shopAvgSpend', 'shopSaleInfo'].includes(item.type)));

/**
   * 计算动态标签所在的行数
   * @returns
   */
const getDynamicTagLineIndex = (content: any) => {
  const hasDynamicTag = content.some((subArray) => subArray.some((item) => item.type === 'dynamicTags'));
  if (!hasDynamicTag) {
    return -1;
  }
  // 存储dynamicTags元素的索引
  let dynamicTagsIndex = content.length - 1;
  // 遍历二维数组，找到dynamicTags元素的索引
  content.forEach((row, rowIndex) => {
    if (rowIndex) {
      row.forEach((item) => {
        if (item.type === 'dynamicTags') {
          const arrLength = content[rowIndex].length;
          // 说明当前这行只有动态标签
          if (arrLength === 1) {
            dynamicTagsIndex = rowIndex;
          }
        }
      });
    }
  });
  return dynamicTagsIndex;
};

/**
   * 计算店铺名称应该展示一行还是两行
   * 1.三星某些特定机型在计算中会有问题，所以统一改成店铺名称一行
   * 2.香港：存在即将休息中则最多展示一行；沙特：存在评分/人均/销量则展示一行，即将休息中占据额外一行
   * @returns number
   */
export const getShopNameMaxLines = (content: any) => {
  if (isUnusualMobileType()) {
    return 1;
  } else {
    const SLCompass = Mach.requireModule('SLCompass');
    const currentRegion = SLCompass?.getCompassInfo?.()?.region || '';
    if (isLogicFollowSA(currentRegion)) {
      return hasScoreLine(content) ? 1 : 2;
    } else {
      return hasNearRestTime(content) ? 1 : 2;
    }
  }
};

/**
   * 计算动态标签应该展示一行还是两行
   * 1.有即将休息-店铺名称最多一行，否则为两行；
   * 2.店铺名称 + 即将休息标识 + 店铺信息有数据 + 配送信息有数据的情况下，动态标签收起情况下最多展示一行，否则最多展示两行
   * 3.三星某些特定机型在计算中会有问题，所以统一改成店铺名称一行
   * @returns boolean
   */
export const shouldDynamicTagsTwoLines = (element: any) => {
  const content = element?.json_data?.content;
  const cardLayout = element?.layout_info;
  const {
    margin_left = 12, margin_right = 12, padding_left = 10, padding_right = 10
  } = cardLayout || {};
  // 如果没有动态标签，那就不用算了
  const hasDynamicTag = content.some((subArray) => subArray.some((item) => item.type === 'dynamicTags'));
  if (!hasDynamicTag) {
    return true;
  }

  // 存储dynamicTags元素的索引
  const dynamicTagsIndex = getDynamicTagLineIndex(content);

  // 开始算有动态标签的情况下
  let realWidth = 0;
  const frameWidth = Mach.env.screenWidth - margin_left - margin_right - padding_left - padding_right - picWidth - 30;
  const SLCommon = Mach.requireModule('SLCommon');
  let shopNameWithIcon = replenishShopNameWithIcon(content);
  shopNameWithIcon = getShopName(shopNameWithIcon);
  if (shopNameWithIcon.featureLabel) {
    realWidth += shopNameWithIcon.featureLabel.width + 4;
  }
  if (shopNameWithIcon.pickUpIcon) {
    realWidth += shopNameWithIcon.pickUpIcon.width + 4;
  }
  const titleWidth = SLCommon.widthForText({
    fontFamily: 'KeeTa-Bold',
    content: getShopName(content).name,
    fontSize: 16,
    bold: 1,
  });
  realWidth += titleWidth;
  const fixWidth = Mach.env.isIOS ? 2 : 6.5; // 一些机型宽度可能会算不准，这里加大下修正值
  let titleLines = (realWidth + fixWidth) < frameWidth ? 1 : 2;
  // 店铺名称行数限制判断
  titleLines = Math.min(titleLines, getShopNameMaxLines(content));
  // 判断店铺名称和动态标签中间有多少行
  let middleLines = 0;
  for (let i = 1; i < dynamicTagsIndex; i++) {
    if (content[i].length > 0) {
      middleLines++;
    }
  }
  // 有菜品卡片情况
  const subCards = element.json_data?.subCards;
  if (subCards && Array.isArray(subCards) && subCards.length > 0) {
    return (titleLines + middleLines < 3);
  }

  return (titleLines + middleLines < 4);
};

export const shouldShowMoreDynamicTags = (content: any, cardLayout: any, realMaxLines = 1) => {
  const {
    margin_left = 12, margin_right = 12, padding_left = 10, padding_right = 10
  } = cardLayout || {};
  const realWidth = Mach.env.screenWidth - margin_left - margin_right - padding_left - padding_right - picWidth - 30;
  const cardDynamicTags = content.flatMap((row) => row.filter((item) => item.type === 'dynamicTags'));
  let shouldShowMore = false;
  if (cardDynamicTags && cardDynamicTags.length > 0) {
    const realDynamicData = cardDynamicTags[0].data.promotionTags.discountTag;
    shouldShowMore = !completeRenderInMaxLines(realDynamicData, realMaxLines, realWidth);
  }
  return shouldShowMore;
};
/**
 * 当前卡片的信息宽度是否能在最大行数限制内展示的下
 * @param tagGroup 动态标签全部数据
 * @param realMaxLines 通过卡片其他信息算出来的动态标签初始最大行数
 * @param realWidth 动态标签占据的真实宽度
 * @returns
 */
export const completeRenderInMaxLines = (tagGroup: IDynamicTagGroup, realMaxLines: number, realWidth: number, tagArr?: string[]) => {
  const { tags = [], tagSpace } = tagGroup;
  const eachTagWidth: number[] = []; //每一个标签的宽度记录

  if (tags.length > 0) {
    for (let singleTagIndex = 0; singleTagIndex < tags.length; singleTagIndex++) {
      let singleTagWidth = 0;
      // 一个tag中有多个子tag
      const { subTags } = tags[singleTagIndex];
      for (let singleSubTagIndex = 0; singleSubTagIndex < subTags.length; singleSubTagIndex++) {
        const singleSubTag = subTags[singleSubTagIndex];
        switch (true) {
          // 文案
          case (singleSubTag.type === 0 || singleSubTag.type === 3):
            singleTagWidth += Number(singleSubTag.leftSpace || 0) + Number(singleSubTag.rightSpace || 0) + Number(singleSubTag.horizontalPadding || 0) + Number(singleSubTag.horizontalPadding || 0) + calcuTitleWidth(singleSubTag);
            break;
            // 图片
          case singleSubTag.type === 1 || singleSubTag.type === 2:
            singleTagWidth += Number(singleSubTag.leftSpace || 0) + Number(singleSubTag.rightSpace || 0) + Number(singleSubTag.horizontalPadding || 0) * 2 + Number(singleSubTag.width || 0);
            break;
          default:
            break;
        }
      }
      eachTagWidth[singleTagIndex] = Mach.env.isAndroid ? singleTagWidth + 4 : singleTagWidth;
    }
  } else if (tagArr && tagArr.length > 0) {
    for (let singleTagIndex = 0; singleTagIndex < tagArr.length; singleTagIndex++) {
      let singleTagWidth = 0;
      singleTagWidth += 32 + +SLCommon.widthForText({
        fontFamily: 'KeeTa-Regular',
        content: tagArr[singleTagIndex],
        fontSize: 14,
      });
      eachTagWidth[singleTagIndex] = singleTagWidth;
    }
  }
  // 获得了所有tag的宽度
  // 开始计算几行
  let lineCount = 0;
  let leftLineWidth = realWidth;
  const tagMargin = tagSpace || 10;
  for (let singleTagIndex = 0; singleTagIndex < eachTagWidth.length; singleTagIndex++) {
    if ((leftLineWidth === realWidth)) {
      // 如果是首个，且tag宽度大于了当前宽度，则占整行并换行
      if (leftLineWidth < eachTagWidth[singleTagIndex]) {
        lineCount += 1;
      } else {
        // 如果是首个，且tag宽度小于当前宽度，则占一行且减剩余宽度
        lineCount += 1;
        leftLineWidth -= (eachTagWidth[singleTagIndex] + tagMargin);
      }
    } else {
      if (leftLineWidth < eachTagWidth[singleTagIndex]) {
        // 如果非首个，且tag宽度大于当前宽度, 那么这个标签要新起一行，减宽度。
        lineCount += 1;
        leftLineWidth = realWidth - (eachTagWidth[singleTagIndex] + tagMargin);
      } else {
        // 如果非首个，且tag宽度小于当前宽度，则不换行，减宽度。
        leftLineWidth -= (eachTagWidth[singleTagIndex] + tagMargin);
      }
    }
  }
  if (lineCount > realMaxLines) {
    // 当前没展示完，需要有展开按钮
    return false;
  } else {
    // 已经在当前渲染的空间里全部展示了，不需要有箭头
    return true;
  }
};

/**
 * 在列表中插入一个字符串保证展开收起icon的展示
 * @param tagArr 原数组列表
 * @param realMaxLines 实际最大行数
 * @param realWidth 实际展示宽度
 * @returns
 */
export const insertExpandIcon = (tagArr: string[], realMaxLines: number, realWidth: number) => {
  if (!tagArr || tagArr.length === 0) {
    return tagArr;
  }
  if (completeRenderInMaxLines([], realMaxLines, realWidth, tagArr)) {
    return tagArr;
  }
  let index: undefined | number;
  let remainingWidth = realWidth - 31;
  for (let i = 0; i < tagArr.length; i++) {
    remainingWidth -= 42 + +SLCommon.widthForText({
      fontFamily: 'KeeTa-Regular',
      content: tagArr[i],
      fontSize: 14,
    });
    if (remainingWidth < 42) {
      index = i + 1;
      break;
    }
  }
  if (index !== undefined) {
    const newArray = [
      ...tagArr.slice(0, index),
      'expand-icon-for-UI',
      ...tagArr.slice(index)
    ];

    console.log('newArray', index, newArray);
    return newArray;
  } else {
    return tagArr;
  }
};

const calcuTitleWidth = (singleSubTag: any) => {
  const titleWidth = SLCommon.widthForText({
    fontFamily: singleSubTag.fontFace,
    content: singleSubTag.text,
    fontSize: Number(singleSubTag.fontSize || 11),
  });
  return titleWidth;
};
